import React, {useEffect} from "react";
import {EventPriceDetails} from "../../../stores/domainStore/pages/models/eventPriceDetails";
import {useTranslation} from "react-i18next";
import {EventPaidType} from "../../../shared/enums/eventPaidType";
import "./eventPriceEdit.sass";
import {ObservableList} from "../../../stores/observables/observableList";
import {SimpleRadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/simpleRadioGroupKeyValue";
import {RadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/radioGroupKeyValue";
import RadioGroupEdit from "../../molecules/radioGroupEdit/radioGroupEdit";
import Banner from "../../atoms/banner/banner";
import {useVillageFeatureToggle} from "../../../utils/featureToggle/villageFeatureToggle";
import {ProcessedBookingsFeatureName} from "../../../shared/constants";
import {IconSource} from "../../../shared/enums/iconSource";
import {Col, Row} from "reactstrap";
import NumberEdit from "../numberEdit/numberEdit";
import FadeContent from "../../molecules/fadeContent/fadeContent";
import DropdownInput from "../../atoms/input/dropdownInput";
import {observer} from "mobx-react-lite";
import EditField from "../../molecules/editField/editField";
import {KeyValue} from "../../../stores/observables/keyValue";
import {EventCostAllocation} from "../../../shared/enums/eventCostAllocation";

export interface IEventPriceEditProps {
  value: EventPriceDetails;
  hasPaidAttendees?: boolean;
  onSelect?: (type: string) => void;
  isMultiVillageEvent?: boolean;
  disableFixedPrice?: boolean;
  disableNonFixedPricePaidType?: boolean;
  disableCostAllocation?: boolean;
  isRequired?: boolean;
}

const EventPriceEdit: React.FC<IEventPriceEditProps> = ({
  value,
  hasPaidAttendees,
  onSelect,
  isMultiVillageEvent,
  disableFixedPrice = false,
  disableNonFixedPricePaidType = false,
  disableCostAllocation = false,
  isRequired = true
}) => {
  const transactionsFeatureToggle = useVillageFeatureToggle(
    ProcessedBookingsFeatureName
  );

  const {t} = useTranslation(["event"]);

  const items: RadioGroupKeyValue[] = [
    new SimpleRadioGroupKeyValue(
      EventPaidType.free,
      t("event:labels.price.free"),
      "",
      disableNonFixedPricePaidType
    ),
    new SimpleRadioGroupKeyValue(
      EventPaidType.ownExpense,
      t("event:labels.price.ownExpense"),
      "",
      disableNonFixedPricePaidType
    )
  ];

  if (!isMultiVillageEvent) {
    items.push(
      new SimpleRadioGroupKeyValue(
        EventPaidType.fixed,
        t("event:labels.price.fixed"),
        "",
        disableFixedPrice
      )
    );
  }

  useEffect(() => {
    value.setHasTransactionsFeature(transactionsFeatureToggle.hasFeature.value);
  }, [transactionsFeatureToggle.hasFeature.value, value]);

  // The items which show in the radio group
  const priceRadioGroupItems = new ObservableList<RadioGroupKeyValue>(
    items,
    value.paidType.text,
    () => value.isValid
  );

  const formatCostAllocationLabel = (value: KeyValue): string | undefined => {
    switch (value.value) {
      case EventCostAllocation.EnhancedActivities:
        return t("event:labels.costAllocation.enhancedActivities");
      case EventCostAllocation.Bar:
        return t("event:labels.costAllocation.bar");
      case EventCostAllocation.FineDining:
        return t("event:labels.costAllocation.fineDining");
    }
  };

  return (
    <div className="event-price">
      <RadioGroupEdit
        items={priceRadioGroupItems}
        label={t("event:labels.price.pricing")}
        name="pricing"
        onSelect={(item: RadioGroupKeyValue) => {
          priceRadioGroupItems.setIsDirty(true);
          value.paidType.setText(item.value);
          onSelect?.(item.value);
        }}
        isRequired={value.validate != null && isRequired}
        isValid={value.isValid}
        bannerText={
          hasPaidAttendees ? t("event:labels.price.paidForEvent") : ""
        }
        isDisabled={hasPaidAttendees}
      />
      <FadeContent fadeContent={value.paidType.text !== EventPaidType.fixed}>
        <Row>
          <Col md={6}>
            <NumberEdit
              value={value.price}
              label={t("event:labels.price.cost")}
              prefix={t("event:labels.price.currency")}
              helpText={t("event:labels.price.priceHelpText")}
              disabled={hasPaidAttendees}
              validationMessage={t("event:errors.price", {
                min: value.price.min!.toString(),
                max: value.price.max!.toString()
              })}
            />
          </Col>
          {transactionsFeatureToggle.hasFeature.value && (
            <Col md={6}>
              <EditField
                label={t("event:labels.price.costAllocation")}
                isRequired={true}
                helpText={t("event:labels.price.costAllocationHelpText")}
              >
                <DropdownInput
                  value={value.costAllocation}
                  errorText={t("event:errors.costAllocation")}
                  labelFormatter={formatCostAllocationLabel}
                  disabled={disableCostAllocation}
                />
              </EditField>
            </Col>
          )}
        </Row>
      </FadeContent>

      {value.paidType.text === EventPaidType.fixed && (
        <Banner
          text={t("event:labels.price.fixedPriceEvent")}
          icon={IconSource.info}
        />
      )}
    </div>
  );
};
export default observer(EventPriceEdit);
